import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';

// ─────────────────────────────────────────────────────────────────────────────
// Invoice Archive — every scanned supplier invoice, no Google login needed
// (db_schema/invoice_archive.sql, 33_drive_invoice_organiser/)
//
// Paper invoices are scanned on the scanner PC; 33a OCRs them, splits them one
// invoice per PDF and files them in the private 'invoices' Storage bucket,
// recording each in drive_invoice_log. Pages are viewed through short-lived
// signed URLs. Search spans every month; with no search, one month is shown
// with its per-supplier totals (what the Drive register sheet used to hold).
// ?q=<invoice no> opens straight onto a search — the Cin7 PO note links here.
// ─────────────────────────────────────────────────────────────────────────────

const BUCKET = 'invoices';

const fmtMoney = (v) => (v == null ? '' : `$${Number(v).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtMonth = (m) => (m && m.length === 6
  ? new Date(`${m.slice(0, 4)}-${m.slice(4)}-01T12:00:00`).toLocaleDateString('en-NZ', { month: 'long', year: 'numeric' })
  : m || 'Undated');
const fmtDate = (iso) => new Date(iso).toLocaleDateString('en-NZ', {
  timeZone: 'Pacific/Auckland', day: 'numeric', month: 'short', year: 'numeric',
});

// ── PDF viewer ───────────────────────────────────────────────────────────────
const Viewer = ({ doc, onClose }) => {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let live = true;
    setUrl(null);
    setErr(null);
    if (!doc.path) {
      setErr('No stored copy.');
      return undefined;
    }
    supabase.storage.from(BUCKET).createSignedUrl(doc.path, 3600).then(({ data, error }) => {
      if (!live) return;
      if (error) setErr(error.message); else setUrl(data?.signedUrl || null);
    });
    return () => { live = false; };
  }, [doc]);

  return (
    <div style={{ position: 'sticky', top: 16, border: '1px solid var(--border)', borderRadius: 6,
                  background: 'var(--bg2)', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                    borderBottom: '1px solid var(--border)' }}>
        <span style={{ fontSize: 13, fontWeight: 700, flex: 1, minWidth: 0, overflow: 'hidden',
                       textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.title}</span>
        {url && (
          <a className="action-btn action-btn--ghost" style={{ fontSize: 11, padding: '3px 10px' }}
             href={url} target="_blank" rel="noreferrer">Open in new tab</a>
        )}
        <button className="action-btn action-btn--ghost" style={{ fontSize: 11, padding: '3px 10px' }} onClick={onClose}>
          Close
        </button>
      </div>
      {url ? (
        <iframe key={url} src={url} title={doc.title} style={{ flex: 1, border: 0, width: '100%', background: '#fff' }} />
      ) : (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: err ? '#dc2626' : 'var(--muted)', fontSize: 12, padding: 16, textAlign: 'center' }}>
          {err || 'Loading…'}
        </div>
      )}
    </div>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────
const Invoices = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [summary, setSummary]   = useState([]);   // get_invoice_archive_months rows
  const [month, setMonth]       = useState(null);
  const [search, setSearch]     = useState(params.get('q') || '');
  const [query, setQuery]       = useState(params.get('q') || '');  // debounced
  const [rows, setRows]         = useState([]);
  const [supplier, setSupplier] = useState('all');
  const [tab, setTab]           = useState('invoices');
  const [unmatched, setUnmatched] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [doc, setDoc]           = useState(null);

  // Months + per-supplier totals
  useEffect(() => {
    supabase.rpc('get_invoice_archive_months').then(({ data, error: e }) => {
      if (e) { setError(e.message); return; }
      setSummary(data || []);
      setMonth(m => m || data?.[0]?.month_folder || null);
    });
  }, []);

  const months = useMemo(() => [...new Set(summary.map(r => r.month_folder))], [summary]);

  useEffect(() => {
    const t = setTimeout(() => {
      setQuery(search.trim());
      setParams(search.trim() ? { q: search.trim() } : {}, { replace: true });
    }, 300);
    return () => clearTimeout(t);
  }, [search, setParams]);

  const load = useCallback(async () => {
    if (!query && !month) return;
    setLoading(true);
    setError(null);
    const { data, error: e } = await supabase.rpc('search_invoice_archive',
      query ? { p_search: query } : { p_month: month });
    if (e) setError(e.message);
    setRows(data || []);
    setLoading(false);
  }, [query, month]);

  useEffect(() => { load(); }, [load]);

  // Unmatched pages live in Storage only (no log row): unmatched/<YYYYMM>/ and unmatched/drive/
  const loadUnmatched = useCallback(async () => {
    const folders = [...(month ? [`unmatched/${month}`] : []), 'unmatched/drive'];
    const lists = await Promise.all(folders.map(f =>
      supabase.storage.from(BUCKET).list(f, { limit: 1000, sortBy: { column: 'created_at', order: 'desc' } })
        .then(({ data }) => (data || []).filter(o => o.id).map(o => ({ ...o, path: `${f}/${o.name}`, folder: f })))));
    setUnmatched(lists.flat());
  }, [month]);

  useEffect(() => { if (tab === 'unmatched') loadUnmatched(); }, [tab, loadUnmatched]);

  const monthSummary = useMemo(() => summary.filter(r => r.month_folder === month), [summary, month]);
  const suppliers = useMemo(() => [...new Set(rows.map(r => r.supplier))].sort(), [rows]);
  const shown = useMemo(() => (supplier === 'all' ? rows : rows.filter(r => r.supplier === supplier)), [rows, supplier]);

  const openRow = (r) => setDoc({
    key: `r${r.id}`, path: r.storage_path,
    title: `${r.supplier} — ${r.invoice_number}`,
  });

  return (
    <main className="app-main">
      <div className="page-nav">
        <button className="back-btn" onClick={() => navigate('/dispatch-tools')}>← Dispatch Tools</button>
        <span className="page-nav__title">Invoice Archive</span>
      </div>

      <div style={{ padding: '16px 24px', display: 'grid', gap: 16,
                    gridTemplateColumns: doc ? 'minmax(0, 1fr) minmax(360px, 46%)' : '1fr' }}>
        <div style={{ minWidth: 0 }}>
          <div className="dash-panel">
            <div className="dash-panel__header" style={{ flexWrap: 'wrap', gap: 8 }}>
              <span className="dash-panel__title">🧾 Scanned Supplier Invoices</span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <input value={search} onChange={e => setSearch(e.target.value)}
                       placeholder="Search invoice #, supplier or order ref (all months)"
                       style={{ width: 300, fontSize: 12, padding: '5px 8px',
                                border: '1px solid var(--border)', borderRadius: 4 }} />
                {search && (
                  <button className="action-btn action-btn--ghost" style={{ fontSize: 11, padding: '3px 10px' }}
                          onClick={() => setSearch('')}>Clear</button>
                )}
                <select value={month || ''} onChange={e => { setMonth(e.target.value); setSearch(''); setSupplier('all'); }}
                        disabled={!!query && tab === 'invoices'} style={{ fontSize: 12, padding: '4px 6px' }}>
                  {months.map(m => <option key={m} value={m}>{fmtMonth(m)}</option>)}
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, padding: '10px 16px', borderBottom: '1px solid var(--border)' }}>
              <button className={`action-btn ${tab === 'invoices' ? 'action-btn--active' : 'action-btn--ghost'}`}
                      style={{ fontSize: 11, padding: '3px 12px' }} onClick={() => setTab('invoices')}>
                Invoices
              </button>
              <button className={`action-btn ${tab === 'unmatched' ? 'action-btn--active' : 'action-btn--ghost'}`}
                      style={{ fontSize: 11, padding: '3px 12px' }} onClick={() => setTab('unmatched')}>
                Unmatched pages
              </button>
            </div>

            {error && (
              <div style={{ padding: '8px 16px', fontSize: 12, color: '#dc2626', borderBottom: '1px solid var(--border)' }}>
                {error}
              </div>
            )}

            {tab === 'invoices' && (
              <>
                {!query && monthSummary.length > 0 && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '10px 16px',
                                borderBottom: '1px solid var(--border)' }}>
                    {monthSummary.map(s => (
                      <button key={s.supplier}
                              className={`action-btn ${supplier === s.supplier ? 'action-btn--active' : 'action-btn--ghost'}`}
                              style={{ fontSize: 11, padding: '4px 10px', textAlign: 'left' }}
                              onClick={() => setSupplier(supplier === s.supplier ? 'all' : s.supplier)}>
                        <strong>{s.supplier}</strong> · {s.invoices} · {fmtMoney(s.total)}
                      </button>
                    ))}
                  </div>
                )}
                {query && suppliers.length > 1 && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '10px 16px',
                                borderBottom: '1px solid var(--border)' }}>
                    {['all', ...suppliers].map(s => (
                      <button key={s} className={`action-btn ${supplier === s ? 'action-btn--active' : 'action-btn--ghost'}`}
                              style={{ fontSize: 11, padding: '3px 10px' }} onClick={() => setSupplier(s)}>
                        {s === 'all' ? 'All suppliers' : s}
                      </button>
                    ))}
                  </div>
                )}

                {loading ? (
                  <div className="loading-row">Loading…</div>
                ) : !shown.length ? (
                  <div className="empty-row">{query ? `No invoices matching “${query}”.` : 'No invoices this month.'}</div>
                ) : (
                  <div className="table-wrap">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Supplier</th><th>Invoice #</th><th>Order ref</th><th className="num">Total</th>
                          <th>Filed</th><th>Cin7 PO</th><th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {shown.map(r => (
                          <tr key={r.id} className="data-row" onClick={() => openRow(r)}
                              style={{ background: doc?.key === `r${r.id}` ? 'var(--bg3)' : undefined }}>
                            <td>{r.supplier}</td>
                            <td className="bold">{r.invoice_number}</td>
                            <td className="muted">{r.order_ref}</td>
                            <td className="num">{fmtMoney(r.total)}</td>
                            <td className="muted">{fmtDate(r.processed_at)}</td>
                            <td>{(r.po_numbers || []).join(', ') || <span className="muted">—</span>}</td>
                            <td>
                              <button className="action-btn action-btn--ghost" style={{ fontSize: 11, padding: '2px 10px' }}
                                      onClick={(e) => { e.stopPropagation(); openRow(r); }}>
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {rows.length === 500 && (
                      <div className="empty-row">Showing the first 500 — narrow the search.</div>
                    )}
                  </div>
                )}
              </>
            )}

            {tab === 'unmatched' && (
              <>
                <div style={{ padding: '10px 16px', fontSize: 11, color: 'var(--muted)', lineHeight: 1.6,
                              borderBottom: '1px solid var(--border)' }}>
                  Pages the organiser couldn't match to a supplier or read an invoice number from, for{' '}
                  {fmtMonth(month)} (plus any copied from the old Drive "unmatched" folder). Usually a page
                  scanned crooked or a supplier with no template yet — rescan it, or ask for a template.
                </div>
                {!unmatched.length ? (
                  <div className="empty-row">No unmatched pages.</div>
                ) : (
                  <div className="table-wrap">
                    <table className="data-table">
                      <thead><tr><th>File</th><th>Folder</th><th>Added</th><th></th></tr></thead>
                      <tbody>
                        {unmatched.map(o => (
                          <tr key={o.path} className="data-row"
                              onClick={() => setDoc({ key: o.path, path: o.path, title: o.name })}
                              style={{ background: doc?.key === o.path ? 'var(--bg3)' : undefined }}>
                            <td className="bold">{o.name}</td>
                            <td className="muted">{o.folder === 'unmatched/drive' ? 'Old Drive folder' : fmtMonth(month)}</td>
                            <td className="muted">{o.created_at ? fmtDate(o.created_at) : ''}</td>
                            <td>
                              <button className="action-btn action-btn--ghost" style={{ fontSize: 11, padding: '2px 10px' }}>
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {doc && <Viewer doc={doc} onClose={() => setDoc(null)} />}
      </div>
    </main>
  );
};

export default Invoices;
